import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing, Colors } from '@/shared/theme';
import { Button, Title, Text, CenteredColumn } from '@/shared/ui';
import { RgBody, RgFoot } from '@/shared/borisFields';
import { PhaseProgressBar } from '@/components/PhaseProgressBar';
import SelectionCard from '@/shared/SelectionCard';
import { TipBox } from '@/shared/TipBox';
import { StartOverModal } from '@/components/StartOverModal';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useEncryptedEventsExternal } from '@/hooks/useEncryptedEventsExternal';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { useHealthConditionsStage } from '@/hooks/useHealthConditionsStage';
import { useEligibilityFinalize } from '@/hooks/useEligibilityFinalize';
import type {
  HealthConditionOption,
  CKMConditionOption,
  BehavioralHealthConditionOption,
} from '@/types/registration';
import {
  ENCRYPTED_BUTTON_PRESSED_ECKM_CONTINUE,
  ENCRYPTED_BUTTON_PRESSED_CKM_CONTINUE,
  ENCRYPTED_BUTTON_PRESSED_BEHAVIORAL_HEALTH_CONTINUE,
} from '@/consts/encryptedEvents';
import securityIcon from '@/assets/images/security.svg';

type AnyConditionOption =
  | HealthConditionOption
  | CKMConditionOption
  | BehavioralHealthConditionOption;

type StepKey =
  | typeof STEP_KEYS.ECKM_CONDITIONS
  | typeof STEP_KEYS.CKM_CONDITIONS
  | typeof STEP_KEYS.BEHAVIORAL_HEALTH;

interface HealthConditionsStepProps {
  stepKey: StepKey;
}

// RND-36523: the steps that make up the filtered questionnaire. handleContinue
// advances within this set (skipping CMS-ineligible screens), then finalizes;
// it must not walk into the always-required post-questionnaire steps.
const QUESTIONNAIRE_STEPS: string[] = [
  STEP_KEYS.ECKM_CONDITIONS,
  STEP_KEYS.CKM_CONDITIONS,
  STEP_KEYS.BEHAVIORAL_HEALTH,
  STEP_KEYS.PHQ9_SCREENER,
  STEP_KEYS.GAD7_SCREENER,
];

// Boris `.rg-screen`: fills the frame's main column (flex:1/min-height:0
// against Layout's FlowMain) and splits into RgBody (scrolls) + RgFoot (pinned
// flex sibling); see borisFields.tsx for why that split replaces the retired
// `position:sticky` foot (the old sticky Footer here used to bleed the "None
// of these" / last condition card through its fade and let the CTA overlap
// the list on scroll).
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const HeadingWrap = styled.div`
  width: 100%;
  text-align: left;
`;

const SubTitleWrapper = styled.div`
  margin-top: ${Spacing.x1_5}px;
  margin-bottom: ${Spacing.x1_5}px;
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Spacing.x3}px;
`;

const OptionsList = styled.div`
  width: 100%;
  max-width: 560px;
  margin: ${Spacing.x2}px auto 0;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1_5}px;
`;

const QuestionBox = styled.div`
  width: 100%;
  max-width: 560px;
  margin: ${Spacing.x1}px auto;
`;

const ErrorText = styled(Text)`
  color: ${Colors.error};
  margin-top: ${Spacing.x1}px;
`;

const HealthConditionsStep: React.FC<HealthConditionsStepProps> = ({ stepKey }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registrationData } = useRegistration();
  const [showError, setShowError] = useState(false);
  const [noneSelected, setNoneSelected] = useState(false);
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const { trackView, track } = useAnalytics();
  const { trackEncryptedEvent } = useEncryptedEventsExternal();
  // RND-36523: the CMS eligibility check runs up front (MedicareCoverageStep), so
  // condition screening no longer fires a submission. When the questionnaire is
  // exhausted, useEligibilityFinalize computes the final track assignment
  // (CMS-eligible ∩ condition-confirmed) with no additional CMS call.
  const { finalizeAndRoute } = useEligibilityFinalize();

  // Derive stage from stepKey for useHealthConditionsStage hook
  const stage = useMemo(() => {
    switch (stepKey) {
      case STEP_KEYS.ECKM_CONDITIONS:
        return 'eCKM' as const;
      case STEP_KEYS.CKM_CONDITIONS:
        return 'CKM' as const;
      case STEP_KEYS.BEHAVIORAL_HEALTH:
        return 'BH' as const;
    }
  }, [stepKey]);

  // Get stage configuration from custom hook
  const config = useHealthConditionsStage(stage);
  const data = registrationData[config.dataKey];

  // Integrate useAppFlow with back navigation blocking
  const { resetBlocker, getNextStep } = useAppFlow(stepKey, {
    onBackNavigationBlocked: () => {
      setShowStartOverModal(true);
    },
  });

  // Track screen view on mount
  useEffect(() => {
    trackView({ event: config.screenViewEvent });
  }, [config.screenViewEvent, trackView]);

  // Reset noneSelected state when stage changes
  useEffect(() => {
    setNoneSelected(false);
  }, [stage]);

  // Check if a condition is selected
  const isSelected = (key: string): boolean => {
    if (key === 'noneOfTheAbove') {
      return noneSelected;
    }
    return Array.from(data.selectedConditions).some((condition) => condition === key);
  };

  const handleCheckboxChange = (key: string) => {
    setShowError(false);
    const currentlySelected = isSelected(key);

    if (key === 'noneOfTheAbove') {
      // Toggle "None of the above" state
      if (currentlySelected) {
        // Deselecting "none of the above"
        setNoneSelected(false);
      } else {
        // Selecting "none of the above" - clear all conditions
        config.updateFn({ selectedConditions: new Set() });
        setNoneSelected(true);
      }
    } else {
      // When selecting a condition, clear "none of the above" state
      setNoneSelected(false);

      // Toggle the condition in the Set
      const newSelectedConditions = new Set(data.selectedConditions);
      if (currentlySelected) {
        // Remove from Set
        newSelectedConditions.delete(key as AnyConditionOption);
      } else {
        // Add to Set
        newSelectedConditions.add(key as AnyConditionOption);
      }
      config.updateFn({ selectedConditions: newSelectedConditions });
    }
  };

  // Check if user has made any selection
  const hasSelection = data.selectedConditions.size > 0 || noneSelected;

  const handleContinue = () => {
    const gatingAnswer = Array.from(data.selectedConditions);
    const isGatingChosen = data.selectedConditions.size > 0;

    track({
      event: config.buttonPressedEvent,
      properties: {
        source: stage,
        gating_answer: gatingAnswer,
        is_gating_chosen: isGatingChosen,
      },
    });

    if (!hasSelection) {
      setShowError(true);
      return;
    }

    // Encrypted external events tracking
    const encryptedEventName = stage === 'eCKM' ? ENCRYPTED_BUTTON_PRESSED_ECKM_CONTINUE : stage === 'CKM' ? ENCRYPTED_BUTTON_PRESSED_CKM_CONTINUE : ENCRYPTED_BUTTON_PRESSED_BEHAVIORAL_HEALTH_CONTINUE;
    trackEncryptedEvent({
      event: encryptedEventName,
      properties: {
        selectedConditions: gatingAnswer, // Send as array of strings
        completed: 'true',
      },
    });

    // RND-36523: the CMS $check-eligibility call already ran up front, so there is
    // no submission here. Mark this stage complete, then advance to the next
    // required step — the CMS-driven predicates route to the next eligible
    // condition screen, or (for BH with self-reported depression/anxiety) to the
    // PHQ-9 screener. getNextStep excludes the current step, so the not-yet-flushed
    // `completed` flag does not matter.
    config.updateFn({ ...data, completed: true });
    // Advance only WITHIN the questionnaire (condition screens + screeners). Once
    // the next required step falls outside it — or there is none — finalize the
    // track assignment. This must not defer to getNextStep alone: the
    // post-questionnaire steps (create-account, shipping, download-app) are
    // unconditionally required, so getNextStep would return create-account here and
    // skip finalize, leaving `eligibility` unset (and bouncing the user to landing).
    const next = getNextStep();
    if (next && QUESTIONNAIRE_STEPS.includes(next.name)) {
      navigate(next.route);
    } else {
      finalizeAndRoute();
    }
  };

  const handleStartOver = () => {
    // Navigate to landing page (which will clear all data on mount)
    setShowStartOverModal(false);
    navigate('/');
  };

  return (
    <Wrapper>
      <RgBody>
        {/* eCKM/CKM/BH are all phase 1 (screening), see consts/phases.ts. */}
        <PhaseProgressBar phase={1} />
        <CenteredColumn span={8}>
          <HeadingWrap>
            <Title size="h2">
              {t(`${config.translationNamespace}.title`)}
            </Title>
          </HeadingWrap>

          {config.showSubtitle !== false && (
            <SubTitleWrapper>
              <Text size="p1">
                {t(`${config.translationNamespace}.description1`)}
              </Text>
            </SubTitleWrapper>
          )}

          <QuestionBox>
            <TipBox icon={securityIcon}>
              <Text size="p1" bold>
                {t(`${config.translationNamespace}.question`)}
              </Text>
            </TipBox>
          </QuestionBox>

          {/* WCAG: Group related checkboxes with fieldset/legend equivalent */}
          <OptionsList role="group" aria-label={t(`${config.translationNamespace}.question`)}>
            {config.conditions.map((condition) => (
              <SelectionCard
                key={condition.key}
                value={condition.key}
                title={t(`${config.translationNamespace}.${condition.translationKey}`)}
                selected={isSelected(condition.key)}
                onClick={handleCheckboxChange}
                titleAlign="end"
              />
            ))}

            {/* None of the above option */}
            <SelectionCard
              value="noneOfTheAbove"
              title={t(`${config.translationNamespace}.none-of-the-above`)}
              selected={isSelected('noneOfTheAbove')}
              onClick={handleCheckboxChange}
              titleAlign="end"
              dashed
            />
          </OptionsList>
          {/* WCAG 4.1.3: Status message announced by screen readers */}
          <div role="alert">
            {showError && !hasSelection && (
              <ErrorText size="p3" centered>
                {t('form.validations.selection')}
              </ErrorText>
            )}
          </div>
        </CenteredColumn>
      </RgBody>

      <RgFoot>
        <CenteredColumn span={6}>
          <Button
            text={t('general.continue')}
            onClick={handleContinue}
            onDisabledClick={handleContinue}
            variant={hasSelection ? 'brand-primary' : 'disabled'}
          />
        </CenteredColumn>
      </RgFoot>

      {/* Start Over Modal */}
      <StartOverModal
        isOpen={showStartOverModal}
        onConfirm={handleStartOver}
        onCancel={() => {
          setShowStartOverModal(false);
          resetBlocker();
        }}
      />
    </Wrapper>
  );
};

export { HealthConditionsStep };
